import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import Notification from '../components/Notification';
import auditService, { AuditLogEntry } from '../services/auditService';

const actionLabels: Record<string, string> = {
  create: 'Creación',
  update: 'Actualización',
  delete: 'Eliminación',
  login: 'Login',
  logout: 'Logout',
};

const formatDate = (value: string) => new Date(value).toLocaleString('es-AR');

const SecurityDashboardPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await auditService.getSystemAuditLogs(
        fromDate || undefined,
        toDate || undefined,
      );
      setLogs(data);
    } catch {
      setNotification({ type: 'error', message: 'No se pudieron cargar los logs de seguridad' });
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const entityOptions = useMemo(() => {
    const unique = Array.from(new Set(logs.map((log) => log.entityType)));
    return unique.sort();
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs
      .filter((log) => (entityFilter ? log.entityType === entityFilter : true))
      .filter((log) => (actionFilter ? log.action.toLowerCase() === actionFilter : true))
      .filter((log) => {
        if (!searchTerm) return true;
        return (
          log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.entityType.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.ipAddress.includes(searchTerm)
        );
      })
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [logs, entityFilter, actionFilter, searchTerm]);

  const summary = useMemo(() => {
    const total = logs.length;
    const success = logs.filter((log) => log.success).length;
    const failure = total - success;
    const uniqueUsers = new Set(logs.map((log) => log.userEmail)).size;
    const last24h = logs.filter((log) => {
      const diff = Date.now() - new Date(log.timestamp).getTime();
      return diff <= 1000 * 60 * 60 * 24;
    }).length;
    return { total, success, failure, uniqueUsers, last24h };
  }, [logs]);

  const recentActions = useMemo(() => filteredLogs.slice(0, 6), [filteredLogs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard de Seguridad</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Monitoreá accesos, cambios y logins en tiempo real y accedé al historial completo.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-5">
        {[
          {
            icon: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
            label: 'Total de eventos',
            value: summary.total.toString(),
          },
          {
            icon: <ShieldCheckIcon className="h-5 w-5 text-blue-500" />,
            label: 'Logs en las últimas 24h',
            value: summary.last24h.toString(),
          },
          {
            icon: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
            label: 'Exitosos',
            value: summary.success.toString(),
          },
          {
            icon: <XCircleIcon className="h-5 w-5 text-red-500" />,
            label: 'Errores',
            value: summary.failure.toString(),
          },
          {
            icon: <UserIcon className="h-5 w-5 text-purple-500" />,
            label: 'Usuarios únicos',
            value: summary.uniqueUsers.toString(),
          },
        ].map((card) => (
          <div
            key={card.label}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex items-center space-x-3"
          >
            <div className="p-2 bg-gray-100 dark:bg-gray-900 rounded-full">{card.icon}</div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{card.label}</div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {card.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Segmentá por funcionalidad, tipo de acceso y rango de fechas.
            </p>
          </div>
          <button
            onClick={() => loadLogs()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-600 bg-primary-100 hover:bg-primary-200"
          >
            Actualizar
          </button>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Funcionalidad
            </label>
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todas</option>
              {entityOptions.map((entity) => (
                <option key={entity} value={entity}>
                  {entity}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Tipo de acceso
            </label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos</option>
              {Object.keys(actionLabels).map((action) => (
                <option key={action} value={action}>
                  {actionLabels[action]}
                </option>
              ))}
              <option value="login">Logins</option>
              <option value="logout">Logouts</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Buscar
            </label>
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Usuario, IP o detalles"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Desde
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Historial reciente
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Últimos eventos filtrados</p>
          </div>
          <span className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">
            {filteredLogs.length} coincidencias
          </span>
        </header>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            {recentActions.map((log) => (
              <div
                key={log.id}
                className="flex items-start gap-3 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-3"
              >
                <div className="flex-shrink-0 rounded-full bg-primary-50 dark:bg-primary-900/20 p-2">
                  <ShieldCheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-300" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>{log.userEmail}</span>
                    <span>{formatDate(log.timestamp)}</span>
                  </div>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {actionLabels[log.action] || log.action} en {log.entityType}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{log.details}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2">Acción</th>
                  <th className="px-3 py-2">Entidad</th>
                  <th className="px-3 py-2">Usuario</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredLogs.slice(0, 8).map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${log.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                        >
                          {log.action}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {log.entityType}
                      {log.entityId ? ` #${log.entityId}` : ''}
                    </td>
                    <td className="px-3 py-2">{log.userEmail}</td>
                    <td className="px-3 py-2">{log.ipAddress}</td>
                    <td className="px-3 py-2">
                      {log.success ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default SecurityDashboardPage;
